import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SKIP_GLOBAL_ERROR_SNACKBAR } from '@core/interceptors/http-error.interceptor';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import {
  ChannelUsage,
  DeviceChannelConfiguration,
  DeviceModel,
  MeterRole,
  PartnerModel,
  PhaseCount,
  PvForecastingConfig,
  PvForecastingConfigCreate,
  PvForecastingConfigUpdate,
} from '@core/models';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError } from 'rxjs';

export interface PhaseCountReading {
  phaseCount: PhaseCount | null;
  phaseCountFixed?: boolean;
}

// The installation page reads the attributes the device page keeps, from the routes the device
// page reads them from, and the phase count the installation keeps itself. Each value is shown in
// its own place on the card, so every failure is the card's to explain and none reaches the global
// snackbar. The writes go to the routes the device page writes through, and to the installation's
// own phase count; every one fails with an InstallationRefusal that the dialog explains in place.
@Injectable({
  providedIn: 'root',
})
export class InstallationAttributesService {
  private readonly apiService = inject(ApiService);

  device(deviceId: number): Observable<DeviceModel> {
    return this.quietly(`/devices/${deviceId}`);
  }

  phaseCount(deviceId: number): Observable<PhaseCountReading> {
    return this.quietly(`/devices/${deviceId}/phase-count`);
  }

  // Answers 404 for a device without a forecast config, which is most of them.
  pvConfig(deviceId: number): Observable<PvForecastingConfig> {
    return this.quietly(`/pv-forecasting-config/devices/${deviceId}`);
  }

  channelUsage(deviceId: number): Observable<ChannelUsage> {
    return this.quietly('/installations/channel-usage', { deviceId });
  }

  // The partner route only matches a lowercase oid; see CompanyService.
  partner(partnerOid: string): Observable<PartnerModel> {
    return this.quietly(`/partners/${partnerOid.toLowerCase()}`);
  }

  // The device's own stored role; a partner default is never written from here.
  setMeterRole(deviceId: number, meterRole: MeterRole): Observable<unknown> {
    return this.apiService
      .patchRaw(`/devices/${deviceId}/meter-role`, { meter_role: meterRole }, explainedInPlace())
      .pipe(catchError(rethrowAsRefusal));
  }

  // The forecast config's PUT replaces every mutable field, so the others are sent back as read.
  setPvActive(
    deviceId: number,
    config: PvForecastingConfig,
    active: boolean,
  ): Observable<PvForecastingConfig> {
    const body: PvForecastingConfigUpdate = {
      device_index: config.device_index,
      device_type: config.device_type,
      active,
      pv_arrays: config.pv_arrays,
    };
    return this.apiService
      .putRaw<PvForecastingConfig>(
        `/pv-forecasting-config/devices/${deviceId}`,
        body,
        explainedInPlace(),
      )
      .pipe(catchError(rethrowAsRefusal));
  }

  // A device without a forecast config gets one, switched on. Where it has one, the channel and
  // the arrays are replaced and the active flag is kept as it is.
  savePvConfig(
    deviceId: number,
    current: PvForecastingConfig | null,
    change: Omit<PvForecastingConfigUpdate, 'active'>,
  ): Observable<PvForecastingConfig> {
    const context = explainedInPlace();
    const request = current
      ? this.apiService.putRaw<PvForecastingConfig>(
          `/pv-forecasting-config/devices/${deviceId}`,
          { ...change, active: current.active } satisfies PvForecastingConfigUpdate,
          context,
        )
      : this.apiService.postRaw<PvForecastingConfig>(
          '/pv-forecasting-config/devices',
          { ...change, device_id: deviceId, active: true } satisfies PvForecastingConfigCreate,
          { context },
        );
    return request.pipe(catchError(rethrowAsRefusal));
  }

  // The channels the PV forecast dialog offers as its energy meter.
  channelConfiguration(deviceId: number): Observable<DeviceChannelConfiguration> {
    return this.quietly('/devices/channel-configuration', { deviceId });
  }

  private quietly<T>(path: string, params: Record<string, unknown> = {}): Observable<T> {
    return this.apiService.getRaw<T>(
      path,
      params,
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR, true),
    );
  }
}
